import {
  Divider,
  EDividerVariant,
  ETextStyleVariant,
  GeneralManageVehiclesOutlineIcon,
  GeneralMapPinOutlineIcon,
  GeneralSearchIcon,
  Text,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useIsLocalityPage } from '@/hooks/useIsLocalityPage';
import useQueryParams from '@/hooks/useQueryParams';
import { formatDateFullRange } from '@/utility/format-date';
import { isDeliveryModeFromParams } from '@/utility/queryParams';

import { INavbarSearch } from '../../SearchHeader';

interface IProps {
  address: INavbarSearch['address'];
  dates: INavbarSearch['dates'];
  guests: INavbarSearch['guests'];
  onOpenMobileSearch?: () => void;
  variant?: 'minimal' | 'enhanced';
  scrollToSearchFilter?: () => void;
  isSERP?: boolean;
  useModeSwitcher?: boolean;
}

const MobileSearchMobileButton: React.FC<IProps> = ({
  address,
  dates,
  guests,
  onOpenMobileSearch,
  variant,
  scrollToSearchFilter,
  isSERP,
  useModeSwitcher,
}) => {
  const intl = useIntl();
  const queryParams = useQueryParams();
  const { isLocalityPage } = useIsLocalityPage();

  const isEnhanced = variant === 'enhanced';
  const isMinimal = variant === 'minimal';
  const hasVariant = isEnhanced || isMinimal;

  const isDeliveryMode = isDeliveryModeFromParams(queryParams);

  // Choose the appropriate icon based on delivery mode and experiment flag
  let SearchIconComponent = GeneralSearchIcon;
  if (useModeSwitcher) {
    SearchIconComponent = isDeliveryMode
      ? GeneralMapPinOutlineIcon
      : GeneralManageVehiclesOutlineIcon;
  }

  const getAddressButtonLabel = () => {
    if (address) {
      return address;
    }

    if (isDeliveryMode) {
      return intl.formatMessage({
        defaultMessage: 'Search stationary delivery locations',
        id: 'y2blIX',
        description: 'Home Search Widget > Delivery Location Filter Placeholder',
      });
    }
    if (!isDeliveryMode && useModeSwitcher) {
      return intl.formatMessage({
        defaultMessage: 'Pickup location',
        id: 'Yxgq0K',
        description: 'Home Search Widget > Location Filter Placeholder',
      });
    }

    return intl.formatMessage({
      defaultMessage: 'Location',
      id: '7TGFRI',
      description: 'Home Search Widget > Escapes Location Filter Placeholder',
    });
  };

  const formattedDates = useMemo<string | null>(() => {
    if (!dates?.from || !dates?.to) {
      return null;
    }
    const datesLabel = formatDateFullRange(dates.from.toString(), dates.to.toString());

    if (dates.flexible_days) {
      return `${datesLabel} (±${dates.flexible_days})`;
    }

    return datesLabel;
  }, [dates]);

  const totalGuests = (guests?.adults || 0) + (guests?.children || 0) + (guests?.infants || 0) || 0;

  const guestsLabel = intl.formatMessage(
    {
      defaultMessage: '{totalGuests, plural, one {# guest} other {# guests}}',
      id: 'ErPlEg',
      description: 'Global Header > label for guests button on mobile bar',
    },
    {
      totalGuests,
    },
  );

  const datesButtonLabel = formattedDates
    ? formattedDates
    : intl.formatMessage({
        defaultMessage: 'Add dates',
        id: 'zP42BU',
        description: 'Global Header > Placeholder text for dates button on mobile bar',
      });

  const guestsButtonLabel = totalGuests
    ? guestsLabel
    : intl.formatMessage({
        defaultMessage: 'Add guests',
        id: 'k/FNcF',
        description: 'Global Header > Placeholder text for guests button on mobile bar',
      });

  return (
    <div
      className={clsx(
        'grid grid-flow-col h-12 bg-white rounded-full overflow-hidden border border-solid border-neutral-20',
        {
          'border-none': isSERP || isLocalityPage,
        },
      )}>
      {!isEnhanced ? (
        <div className="flex items-center w-full h-full px-4 overflow-hidden bg-white rounded-full gap-3">
          <button onClick={onOpenMobileSearch} className="w-full overflow-hidden text-left">
            <div className="flex flex-row items-center w-full h-full gap-3">
              {!hasVariant && (
                <SearchIconComponent fontSize={useModeSwitcher ? 24 : 21} className="shrink-0" />
              )}
              <div className="flex flex-col items-start truncate">
                {!hasVariant && (
                  <Text className="w-full text-sm font-medium text-left truncate">
                    {getAddressButtonLabel()}
                  </Text>
                )}
                <div className="flex flex-row items-start w-full gap-1">
                  <Text className="text-xs truncate text-gray-50">{datesButtonLabel}</Text>
                  <>
                    <Text className="text-xs truncate text-gray-50">{'•'}</Text>
                    <Text className="text-xs truncate text-gray-50">{guestsButtonLabel}</Text>
                  </>
                </div>
              </div>
            </div>
          </button>
        </div>
      ) : (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="flex items-center w-full h-full pl-4 pr-1 overflow-hidden bg-white rounded-full gap-3"
          onClick={onOpenMobileSearch}>
          <div className="flex flex-row items-center w-full h-full gap-3">
            <Text
              className="flex-1 text-left truncate text-gray-50"
              variant={ETextStyleVariant.LegalBold}>
              {datesButtonLabel}
            </Text>
            <Divider variant={EDividerVariant.Vertical} />
            <Text
              className="flex-1 text-left truncate text-gray-50"
              variant={ETextStyleVariant.LegalBold}>
              {guestsButtonLabel}
            </Text>
            <button
              className="flex items-center justify-center w-10 h-10 bg-gray-800 rounded-full shrink-0"
              onClick={scrollToSearchFilter}>
              <GeneralSearchIcon width={18} height={18} color="white" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSearchMobileButton;
